<template>
  <div class="AudioHandler">
    <div v-for="peer in languagePeers" :key="peer.id" class="languagePeers">
      <AudioElem
        v-if="peer.audioConsumer.ready"
        :volume="isEmbed ? consumeVol : consumeVolume"
        :audio-consumer="peer.audioConsumer"
        :audio-context="audioContext"
      />
    </div>

    <div v-if="listenFloor" class="floorPeers">
      <div v-for="peer in floorPeers" :key="peer.id">
        <AudioElem
          v-if="peer.audioConsumer.ready"
          :volume="isEmbed ? floorVol : floorVolume"
          :audio-consumer="peer.audioConsumer"
          :audio-context="audioContext"
        />
      </div>
    </div>
    <div v-if="hearOtherTranslator" class="otherTranslator">
      <div v-for="peer in onAirBoothPartners" :key="peer.id">
        <AudioElem
          v-if="peer.audioConsumer.ready"
          :volume="otherTranslatorVolume"
          :audio-consumer="peer.audioConsumer"
          :audio-context="audioContext"
        />
      </div>
    </div>
  </div>
</template>
<script>
/**
 * Creates Audio Element components for every user, based on the selected Language
 * @displayName Audio Handler
 */
import { mapState } from "vuex";
import AudioElem from "./AudioElem.vue";
import AwsTranscribe from "../../helpers/aws-socket-transcribe.js";

export default {
  name: "AudioHandler",
  components: {
    AudioElem,
  },
  props: {
    /**
     * Translation partner in the same language configuration
     */
    translatorPartners: Array,
    isEmbed: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      audioContext: null,
      supportedTranscribeLangs: {
        ENG: "en-US",
        DEU: "de-DE",
        ZHO: "zh-CN",
        FRA: "fr-FR",
        ITA: "it-IT",
        SPA: "es-US",
        JPN: "ja-JP",
        KOR: "ko-KR",
        POR: "pt-BR",
      },
      consumeVol: 50,
      floorVol: 50,
    };
  },
  computed: {
    ...mapState({
      peers: (state) => state.mediasoup.peers,
      consumeLang: (state) => state.mediasoup.consumeLang,
      listenFloor: (state) => state.mediasoup.listenFloor,
      consumeVolume: (state) => state.mediasoup.consumeVolume,
      floorVolume: (state) => state.mediasoup.floorVolume,
      listenerMute: (state) => state.mediasoup.listenerMute,
      hearOtherTranslator: (state) => state.mediasoup.hearOtherTranslator,
      otherTranslatorVolume: (state) => state.mediasoup.otherTranslatorVolume,
      noTranslation: (state) => state.mediasoup.noTranslation,
      role: (state) => state.mediasoup.role,
      audioContextDest: (state) => state.audioContextDest,
      transcriptionStarted: (state) => state.mediasoup.transcriptionStarted,
      transcriptionOnClicked: (state) => state.mediasoup.transcriptionOnClicked,
      transcriptionOffClicked: (state) => state.mediasoup.transcriptionOffClicked,
    }),
    /**
     * List of peers that speak this users language
     */
    languagePeers() {
      let langPeers = [];
      langPeers = this.peers.filter(
        (peer) =>
          peer.produceLang === this.consumeLang && ((peer.onAir && !this.noTranslation) || peer.role === "host"),
      );
      console.log("File: AudioHandler.vue, langPeers", langPeers);
      // to visualize the audio in embed link
      this.$emit("lang-peers", langPeers);
      return langPeers;
    },
    /**
     * List of peers that do not speak this users language
     */
    floorPeers() {
      let floorPeers = this.peers.filter(
        (peer) => (peer.role === "host" && peer.produceLang !== this.consumeLang) || peer.role === "hybrid",
      );
      console.log("File: AudioHandler.vue, floorPeers", floorPeers);
      return floorPeers;
    },
    /**
     * The Partner Translator in the same language configuration
     */
    onAirBoothPartners() {
      console.log("File: AudioHandler.vue, partner", this.translatorPartners);
      const onAirPartner = this.translatorPartners.filter((t) => t.onAir === true);
      console.log("File: AudioHandler.vue, onAir partner", onAirPartner);
      return onAirPartner;
      // let onAir = this.translatorPartners.find(t => t.onAir === true);
      // console.log("File: AudioHandler.vue, othertranslator onair", onAir);
      // if (onAir) {
      //   const peer = this.peers.filter(
      //     peer => peer.role === "translator" && peer.id === onAir.mediasoupId
      //   );
      //   console.log("File: AudioHandler.vue, othertranslator", peer);
      //   return peer;
      // } else {
      //   return [];
      // }
    },
    /**
     * The Partner Translators in the same language configuration
     */
    onAirInterpreters() {
      let allPeers = this.$store.getters["mediasoup/getAllPeers"];
      console.log("File: AudioHandler.vue, all peers", allPeers);
      const onAirInterpreters = allPeers.filter((peer) => {
        if (peer.role === "translator" && peer.onAir === true && peer.produceLang === this.consumeLang) {
          return true;
        }
      });
      // console.log("File: AudioHandler.vue, onairtranslator", onAirInterpreters);
      return onAirInterpreters;
    },
  },
  watch: {
    listenerMute(newVal) {
      this.consumeVol = newVal ? 0 : this.consumeVolume;
      this.floorVol = newVal ? 0 : this.floorVolume;
    },
    // Resuming audioContext
    transcriptionOnClicked(newVal) {
      if (newVal && !this.transcriptionStarted) {
        this.resumeContext();
        this.$store.commit("mediasoup/set_transcriptionOnClicked", false);
      }
    },
    // Suspending audioContext
    transcriptionOffClicked(newVal) {
      if (newVal && this.transcriptionStarted) {
        this.suspendContext();
        this.$store.commit("mediasoup/set_transcriptionOffClicked", false);
      }
    },
    // Stoping old consume lang transcription and
    // starting new lang transcription
    consumeLang() {
      if (this.transcriptionStarted) {
        this.suspendContext();
        this.resumeContext();
      }
    },
    onAirInterpreters(newVal) {
      console.log("File: AudioHandler.vue, consume lang:", this.consumeLang);
      console.log("File: AudioHandler.vue, new ON AIR translator:", newVal);
      // Translators handle this in TranslatorOverview.vue
      // To stop original audio in listener uncomment this
      // if (this.role !== "translator" && this.role !== "listener") {
      if (this.role !== "translator") {
        if (newVal.length === 0) {
          // if no ON AIR available, there should be floor sound
          console.log("File: AudioHandler.vue, no translator onair");
          this.$store.commit("mediasoup/set_translatorsOnAir", false);
          this.$store.commit("mediasoup/set_listenFloor", true);
        } else if (this.role === "proxy") {
          // if ON AIR available, there should be no floor sound
          console.log("File: AudioHandler.vue, proxy");
          this.$store.commit("mediasoup/set_translatorsOnAir", true);
          this.$store.commit("mediasoup/set_listenFloor", false);
        } else if (this.role === "listener") {
          // if ON AIR available, there should be no floor sound
          console.log("File: AudioHandler.vue, listener");
          this.$store.commit("mediasoup/set_translatorsOnAir", true);
          this.$store.commit("mediasoup/set_listenFloor", false);
        } else {
          // For roles other than translator, proxy and listener
          // there should be always floor sound activated
          console.log("File: AudioHandler.vue, else");
          this.$store.commit("mediasoup/set_translatorsOnAir", true);
          this.$store.commit("mediasoup/set_listenFloor", true);
        }
      }
      console.log("File: AudioHandler.vue, listenFloor:", this.listenFloor);
    },
  },
  created() {
    console.log("consumeVolume", this.consumeVolume);
    console.log("isEmbed", this.isEmbed);
    // Creating audiocontext for use later
    console.log("File: AudioHandler, audio context", this.audioContext);
    if (!this.audioContext) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      this.audioContext = new AudioContext();
      this.$store.commit("set_audioContextDest", this.audioContext.createMediaStreamDestination());
    }
    // Handling proxy original sound
    if (this.role === "proxy" && this.consumeLang === "ORIG") {
      this.$store.commit("mediasoup/set_listenFloor", true);
    } else if (this.role === "proxy" && this.consumeLang !== "ORIG") {
      this.$store.commit("mediasoup/set_listenFloor", false);
    }
  },
  /**
   * Used for object cleanup
   */
  async beforeDestroy() {
    this.audioContext?.close();
    this.awsTranscriber?.closeConnection();
    await new Promise((r) => setTimeout(r, 2000));
  },
  methods: {
    /**
     * Resumes the audio context after user gesture
     * and initiates AWS transcription
     */
    async resumeContext() {
      console.log("File: AudioHandler.vue, resuming audio context...");
      const supportedLang = Object.keys(this.supportedTranscribeLangs).find((lang) => lang === this.consumeLang);
      if (supportedLang) {
        await this.audioContext.resume();
        console.log("File: AudioHandler.vue, audioContext state", this.audioContext.state);
        await this.startTranscription(supportedLang);
        this.$store.commit("mediasoup/set_transcriptionStarted", true);
      } else {
        this.$buefy.toast.open({
          message: this.$t("subtitleLangNotSupported"),
          type: "is-danger",
        });
      }
    },
    /**
     * Suspends the currently active audioContext and
     * closes the websocket connection to Aws transcribe
     */
    async suspendContext() {
      console.log("File: AudioHandler.vue, suspending audio context...");
      await this.audioContext.suspend();
      console.log("File: AudioHandler.vue, audioContext state", this.audioContext.state);
      this.awsTranscriber.closeConnection();
      this.$store.commit("mediasoup/set_transcriptionStarted", false);
    },
    /**
     * Creates AwsTranscribe object and starts streaming
     * transcription via websocket connection
     */
    async startTranscription(lang) {
      this.awsTranscriber = new AwsTranscribe(this.supportedTranscribeLangs[lang]);
      this.awsTranscriber.startTranscribe(this.audioContextDest.stream);
    },
  },
};
</script>
